import { getCurrentProfile } from '../services/authService';
import { broadcastMayday } from '../services/maydayService';
import { showToast } from '../components/Toast';
import { getDadDisplayName } from '../types';

export function renderMaydayModal(): string {
  return `
    <div id="modal-mayday" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto modal-backdrop animate-fade-in font-sans">
      <div class="bg-[#0a0c13] border border-pink-500/60 rounded-2xl max-w-lg w-full p-5 sm:p-6 shadow-[0_15px_40px_rgba(255,0,85,0.25)] space-y-4 my-8 relative overflow-hidden">
        <div class="laser-line-pink absolute top-0 left-0 right-0"></div>
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-white/[0.08] pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-transparent text-pink-400 flex items-center justify-center font-black border border-pink-500/50 shadow-[0_0_8px_rgba(255,0,85,0.3)]">
              <i class="fa-solid fa-satellite-dish"></i>
            </div>
            <div>
              <h3 class="text-base font-black text-slate-100">Broadcast Crew Mayday</h3>
              <p class="text-xs text-slate-400">15-minute quick-assist peer call to nearby dads</p>
            </div>
          </div>
          <button id="close-mayday-modal-btn" class="text-slate-400 hover:text-white p-1">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <form id="mayday-broadcast-form" class="space-y-4 text-xs">
          
          <div class="space-y-1">
            <label class="block font-bold text-slate-300">What's the immediate problem? *</label>
            <input type="text" id="mayday-text-input" required placeholder="e.g. Need 2 guys to help lift a cast-iron tub onto my truck bed (5 mins)" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-pink-500 focus:shadow-[0_0_10px_rgba(255,0,85,0.25)]">
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1">
              <label class="block font-bold text-slate-300">Urgency Level</label>
              <select id="mayday-urgency-select" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-2.5 py-2 text-slate-300 focus:outline-none focus:border-pink-500">
                <option value="Right now! (Next 20 mins)">Right now! (Next 20 mins)</option>
                <option value="Need today (within 2 hrs)" selected>Need today (within 2 hrs)</option>
                <option value="This afternoon">This afternoon</option>
              </select>
            </div>
            <div class="space-y-1">
              <label class="block font-bold text-slate-300">Neighborhood Area</label>
              <input type="text" id="mayday-area-input" value="Local Neighborhood" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-pink-500">
            </div>
          </div>

          <div class="space-y-1">
            <label class="block font-bold text-slate-300">Gratitude / Reward Note</label>
            <input type="text" id="mayday-reward-input" placeholder="e.g. Cold six-pack of beverage / Eternal brotherhood gratitude" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-pink-500">
          </div>

          <div class="p-3 bg-transparent border border-pink-500/40 rounded-xl text-[11px] text-pink-300/90 leading-snug shadow-[0_0_8px_rgba(255,0,85,0.15)]">
            <i class="fa-solid fa-triangle-exclamation mr-1 text-pink-400"></i>
            Maydays broadcast within a tight 1–3 mile radius and automatically expire after 2 hours to keep the feed fresh.
          </div>

          <div class="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.08]">
            <button type="button" id="cancel-mayday-btn" class="px-4 py-2 rounded-lg text-slate-400 hover:text-white border border-transparent hover:border-white/10 transition-colors">
              Cancel
            </button>
            <button type="submit" class="btn-danger px-5 py-2.5 rounded-xl font-bold flex items-center gap-2">
              <i class="fa-solid fa-tower-broadcast"></i> Send Mayday
            </button>
          </div>

        </form>

      </div>
    </div>
  `;
}

export function bindMaydayModalEvents(
  onSuccess: () => void,
  onCancel: () => void
): void {
  document.getElementById('close-mayday-modal-btn')?.addEventListener('click', () => {
    onCancel();
  });
  document.getElementById('cancel-mayday-btn')?.addEventListener('click', () => {
    onCancel();
  });

  const form = document.getElementById('mayday-broadcast-form') as HTMLFormElement;
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = (document.getElementById('mayday-text-input') as HTMLInputElement).value.trim();
    const urgency = (document.getElementById('mayday-urgency-select') as HTMLSelectElement).value as 'Right now! (Next 20 mins)' | 'Need today (within 2 hrs)' | 'This afternoon';
    const area = (document.getElementById('mayday-area-input') as HTMLInputElement).value.trim();
    const reward = (document.getElementById('mayday-reward-input') as HTMLInputElement).value.trim();

    const profile = getCurrentProfile();
    if (!profile) {
      showToast('Please sign in to broadcast a Mayday.', 'warning');
      return;
    }

    try {
      await broadcastMayday(
        { uid: profile.uid, name: getDadDisplayName(profile), avatarInitials: profile.avatarInitials },
        text,
        urgency,
        area,
        reward
      );

      showToast('Mayday beacon broadcasted to nearby connected dads!', 'mayday');
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to broadcast Mayday.';
      showToast(msg, 'warning');
    }
  });

  const modal = document.getElementById('modal-mayday');
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      onCancel();
    }
  });
}
